import { ArrowLeft, Building2, CheckCircle2, Download, ShieldCheck, User } from "lucide-react";
import { useRef, useState } from "react";
import { VerifyModal } from "./VerifyModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

// ─── THEME — matches GoElectronix brand ──────────────────────────────────────
const ACCENT   = "#2563EB";   // GoElectronix primary blue
const ACCENT_2 = "#EFF6FF";   // light blue tint for alternating rows / sections
const ACCENT_3 = "#1D4ED8";   // darker blue for TOTAL DUE bar

// ─── HELPERS ─────────────────────────────────────────────────────────────────
function toWords(n) {
  const num = Math.round(Number(n || 0));
  if (!num) return "Zero";
  const ones = ["","One","Two","Three","Four","Five","Six","Seven","Eight","Nine",
    "Ten","Eleven","Twelve","Thirteen","Fourteen","Fifteen","Sixteen",
    "Seventeen","Eighteen","Nineteen"];
  const tens = ["","","Twenty","Thirty","Forty","Fifty","Sixty","Seventy","Eighty","Ninety"];
  if (num < 20) return ones[num];
  if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? " "+ones[num%10] : "");
  if (num < 1000) return ones[Math.floor(num/100)]+" Hundred"+(num%100?" "+toWords(num%100):"");
  if (num < 100000) return toWords(Math.floor(num/1000))+" Thousand"+(num%1000?" "+toWords(num%1000):"");
  if (num < 10000000) return toWords(Math.floor(num/100000))+" Lakh"+(num%100000?" "+toWords(num%100000):"");
  return toWords(Math.floor(num/10000000))+" Crore"+(num%10000000?" "+toWords(num%10000000):"");
}

// ─────────────────────────────────────────────────────────────────────────────
export function PayoutSummary({ data, onBack, onVerified }) {

  const stmt     = data || {};
  const bank     = stmt?.entity?.bank || {};
  const products = stmt.products || [];
  const summary  = stmt.summary || {};

  const authUser      = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const gstJson       = authUser?.gst_json;
  const hasGST        = !!gstJson;
  const taxpayerType  = gstJson?.taxpayer_type?.toLowerCase();
  const gstNumber     = gstJson?.GSTIN || authUser?.gst || "";
  const isMaharashtraGST = gstNumber.startsWith("27");

  const subtotal = Number(summary?.subtotal || 0);
  const cgst     = Number(summary?.cgst || 0);
  const sgst     = Number(summary?.sgst || 0);
  const igst     = Number(summary?.igst || 0);
  const tds      = Number(summary?.tds || 0);

  let payableAmount = subtotal - tds;
  if (hasGST && taxpayerType === "regular" && isMaharashtraGST)
    payableAmount = subtotal + cgst + sgst - tds;
  if (hasGST && taxpayerType === "regular" && !isMaharashtraGST)
    payableAmount = subtotal + igst - tds;

  const formatNum = (n) =>
    Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

  const [showModal, setShowModal] = useState(false);
  const pdfRef = useRef(null);

  const handleDownloadPDF = async () => {
  try {
    const input = pdfRef.current;

    if (!input) return;

    // better rendering
    const canvas = await html2canvas(input, {
      scale: 3,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    });

    const imgData = canvas.toDataURL("image/png");

    // A4 PDF
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = 210;
    const pageHeight = 297;

    // margins
    const margin = 10;

    const usableWidth = pageWidth - margin * 2;

    // image dimensions
    const imgWidth = usableWidth;

    const imgHeight =
      (canvas.height * imgWidth) / canvas.width;

    let position = margin;
    let heightLeft = imgHeight;

    // first page
    pdf.addImage(
      imgData,
      "PNG",
      margin,
      position,
      imgWidth,
      imgHeight,
      "",
      "FAST"
    );

    heightLeft -= (pageHeight - margin * 2);

    // multi-page support
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + margin;

      pdf.addPage();

      pdf.addImage(
        imgData,
        "PNG",
        margin,
        position,
        imgWidth,
        imgHeight,
        "",
        "FAST"
      );

      heightLeft -= (pageHeight - margin * 2);
    }

    pdf.save(
      `${stmt?.statement_id || "payout-summary"}.pdf`
    );

  } catch (err) {
    console.error("PDF download failed", err);
  }
};

  return (
    <div className="space-y-4">

      {/* ── ACTION BAR ── */}
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors"
        >
          <ArrowLeft size={16}/> Back to Register
        </button>
        <button
          onClick={handleDownloadPDF}
          className="flex items-center gap-2 text-white text-xs font-bold px-4 py-2 rounded-lg transition-all hover:opacity-90"
          style={{ background: ACCENT, boxShadow: `0 4px 12px ${ACCENT}44` }}
        >
          <Download size={13}/> Download PDF
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════
          PDF PRINTABLE AREA — pdfRef wraps ONLY invoice content (not verify)
      ════════════════════════════════════════════════════════════════════ */}
      <div
        ref={pdfRef}
        className="bg-white border border-slate-200 rounded-xl overflow-hidden"
        style={{
          fontFamily: "'Plus Jakarta Sans','DM Sans',sans-serif",
          fontSize: 12,
          width: "794px", // exact A4 width
          margin: "0 auto",
          background: "#fff",
        }}
      >

        {/* ── TOP HEADER ── */}
        <div className="flex items-start justify-between px-8 pt-8 pb-6">

          {/* Left: Logo + name */}
          <div className="flex items-center gap-3">
            <div
              className="w-50 h-20 rounded-xl overflow-hidden bg-white flex items-center justify-center"
              // style={{ border: `2px solid ${ACCENT}` }}
            >
              <img
                src="https://warrantymitra.com/img/logo.png"
                alt="Warranty Mitra Logo"
                className="w-full h-full object-contain p-1"
              />
            </div>
          </div>

          {/* Right: STATEMENT title */}
          <div className="text-right">
            <p className="font-black tracking-wider leading-none" style={{ fontSize: 24, color: ACCENT }}>
              STATEMENT
            </p>
            <p className="text-[12px] font-semibold text-slate-500 mt-1">
              {stmt?.cycle?.start_date || new Date().toLocaleDateString("en-IN", { day:"2-digit", month:"long", year:"numeric" })}
            </p>
            <p className="text-[10px] font-bold text-slate-400 mt-0.5 font-mono">
              #{stmt?.statement_id || stmt?.payout_code || "—"}
            </p>
          </div>
        </div>

        {/* ── ADDRESSES ── */}
        <div className="grid grid-cols-2 gap-8 px-8 pb-6 border-b border-slate-100">

          {/* TO */}
          <div>
            <p className="text-[10px] font-black uppercase tracking-widest mb-1.5"
              style={{ color: ACCENT }}>
              TO :
            </p>
            <p className="font-bold text-slate-800 text-[13px]">
              {stmt?.entity?.name || "—"}
            </p>
            <p className="text-slate-500 text-[11px] leading-relaxed mt-1">
              Type: {stmt?.entity?.type || "—"}<br/>
              Bank: {bank?.bank_name || "—"}<br/>
              Period: {stmt?.cycle?.start_date} to {stmt?.cycle?.end_date}<br/>
              GSTIN: {stmt?.entity?.gst || "—"}
            </p>
          </div>

          <div>
            <p className="text-[10px] font-black uppercase tracking-widest mb-1.5"
              style={{ color: ACCENT }}>
              Company :
            </p>
            <p className="font-bold text-slate-800 text-[13px]">
              {stmt?.company?.name || "GoElectronix Solutions Pvt Ltd"}
            </p>
            <p className="text-slate-500 text-[11px] leading-relaxed mt-1 whitespace-pre-line">
              {stmt?.company?.address || "Plot No. 42, Tech Park, Sector 11,\nNavi Mumbai, Maharashtra - 400706"}
            </p>
            {/* {stmt?.entity?.gst && (
              <p className="text-[10px] text-slate-400 mt-1">GSTIN: {stmt.entity.gst}</p>
            )} */}
          </div>
        </div>

        {/* ── PRODUCT TABLE ── */}
        <table className="w-full" style={{ borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: ACCENT }}>
              {["#", "Items Description", "HSN/SAC", "Unit Price", "Qnt", "Total"].map((h, i) => (
                <th
                  key={h}
                  className="py-3 px-4 text-[10px] font-black uppercase tracking-widest text-white"
                  style={{ textAlign: i === 0 || i === 1 ? "left" : i === 5 ? "right" : "center" }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products.length > 0
              ? products.map((p, i) => (
                  <tr key={i} style={{
                    borderBottom: "1px solid #F1F5F9",
                    background:   i % 2 === 0 ? "#fff" : ACCENT_2,
                  }}>
                    <td className="py-4 px-4 text-slate-400 font-semibold">{i + 1}</td>
                    <td className="py-4 px-4">
                      <p className="font-bold text-slate-800">{p.product_name}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">Warranty plan</p>
                    </td>
                    <td className="py-4 px-4 text-center text-slate-500 font-mono">—</td>
                    <td className="py-4 px-4 text-center font-mono text-slate-700">₹{formatNum(p.rate)}</td>
                    <td className="py-4 px-4 text-center text-slate-600">{p.qty}</td>
                    <td className="py-4 px-4 text-right font-bold text-slate-800">₹{formatNum(p.amount)}</td>
                  </tr>
                ))
              : [1, 2, 3].map(i => (
                  <tr key={i} style={{
                    borderBottom: "1px solid #F1F5F9",
                    background:   i % 2 !== 0 ? "#fff" : ACCENT_2,
                  }}>
                    <td className="py-4 px-4 text-slate-300">{i}</td>
                    <td className="py-4 px-4"><p className="text-slate-300">—</p></td>
                    <td className="py-4 px-4 text-center text-slate-300">—</td>
                    <td className="py-4 px-4 text-center text-slate-300">—</td>
                    <td className="py-4 px-4 text-center text-slate-300">—</td>
                    <td className="py-4 px-4 text-right text-slate-300">—</td>
                  </tr>
                ))
            }
          </tbody>
        </table>

        {/* ── TOTALS SECTION ── */}
        <div className="flex px-8 py-5 gap-8 border-t border-slate-100">

          {/* Left: Note */}
          <div className="flex-1">
            <p className="text-[10px] font-black uppercase tracking-widest mb-1"
              style={{ color: ACCENT }}>
              NOTE:
            </p>
            <p className="text-[11px] text-slate-500 leading-relaxed max-w-xs">
              Indian Rupee {toWords(payableAmount)} Only
            </p>
          </div>

          {/* Right: breakdown */}
          <div className="w-72">
            {[
              { label: "SUBTOTAL :",      val: formatNum(subtotal), red: false },
              ...(hasGST && taxpayerType === "regular" && isMaharashtraGST
                ? [
                    { label: "Tax CGST 9% :", val: formatNum(cgst), red: false },
                    { label: "Tax SGST 9% :", val: formatNum(sgst), red: false },
                  ]
                : []),
              ...(hasGST && taxpayerType === "regular" && !isMaharashtraGST
                ? [{ label: "IGST 18% :",    val: formatNum(igst), red: false }]
                : []),
              { label: "TDS:",      val: `- ${formatNum(tds)}`, red: true },
            ].map(({ label, val, red }) => (
              <div key={label} className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-[11px] font-semibold text-slate-500">{label}</span>
                <span className={`text-[11px] font-bold font-mono ${red ? "text-red-500" : "text-slate-700"}`}>
                  ₹ {val}
                </span>
              </div>
            ))}

            {/* TOTAL DUE bar */}
            <div
              className="flex justify-between items-center px-4 py-3 mt-1 rounded-lg"
              style={{ background: ACCENT_3 }}
            >
              <span className="text-white font-black text-[13px] uppercase tracking-wide">
                TOTAL :
              </span>
              <span className="text-white font-black text-[15px] font-mono">
                ₹ {formatNum(payableAmount)}
              </span>
            </div>
          </div>
        </div>

        {/* ── PAYMENT TO ── */}
        <div
          className="flex items-start gap-5 px-8 py-4 border-t border-slate-100"
          style={{ background: ACCENT_2 }}
        >
          {/* <div
            className="w-16 h-16 rounded-lg flex items-center justify-center shrink-0 text-[8px] font-bold"
            style={{ border: `2px dashed ${ACCENT}`, color: ACCENT, background: "white" }}
          >
            QR Code
          </div> */}
          <div className="text-[10px] text-slate-500 leading-relaxed">
            <p className="text-[9px] font-black uppercase tracking-widest mb-1" style={{ color: ACCENT }}>
              Payment To
            </p>
            <p className="font-bold text-slate-800 text-[12px]">{stmt?.entity?.name}</p>
            <p className="mt-0.5">{bank.bank_name} · ACC NO.: {bank.account_no}</p>
            <p>IFSC: {bank.ifsc_code} · BRANCH: {bank.branch_name} · ACC TYPE: {bank.account_type}</p>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div
          className="flex items-center justify-between px-8 py-3 border-t border-slate-100"
          style={{ background: ACCENT_2 }}
        >
          <p className="text-[12px] font-black" style={{ color: ACCENT }}>
            Thank you for your Business
          </p>
          {/* <span className="font-black text-slate-900 text-[13px]">
            Go<span style={{ color: ACCENT }}>electronix</span>
          </span> */}
        </div>

      </div>
      {/* ═══════════════ END pdfRef ═══════════════ */}

      {/* ── VERIFY SECTION — OUTSIDE PDF ──────────────────────────────────── */}
      <div
        className="rounded-2xl p-5 flex items-start justify-between gap-4"
        style={{
          background: "white",
          border:     `1px solid ${ACCENT}22`,
          boxShadow:  `0 4px 16px ${ACCENT}12`,
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: `${ACCENT}15` }}
          >
            <ShieldCheck size={18} style={{ color: ACCENT }}/>
          </div>
          <div>
            <p className="text-[13px] font-bold text-slate-800 mb-0.5">
              Summary Verification
            </p>
            <p className="text-[12px] text-slate-500 leading-relaxed">
              Do you agree with this summary? To verify and move this payout to{" "}
              <span className="font-bold text-slate-700">Billed</span>, click the{" "}
              <span className="font-bold" style={{ color: ACCENT }}>Verify Summary</span> button.
            </p>
          </div>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl text-[12px] font-black text-white
            transition-all hover:scale-105 active:scale-95 whitespace-nowrap"
          style={{
            background:  `linear-gradient(135deg, ${ACCENT}, ${ACCENT_3})`,
            boxShadow:   `0 6px 20px ${ACCENT}44`,
          }}
        >
          <CheckCircle2 size={14}/> Verify Summary
        </button>
      </div>

      {/* Verify Modal */}
      {showModal && (
        <VerifyModal
          onClose={() => setShowModal(false)}
          onVerified={onVerified}
          selectedRowId={data.payout_id}
        />
      )}

    </div>
  );
}

// import { ArrowLeft, Building2, CheckCircle2, Download, ShieldCheck, User } from "lucide-react";
// import { useRef, useState } from "react";
// import { VerifyModal } from "./VerifyModal";

// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";

// export function PayoutSummary({ data, onBack, onVerified }) {

//   const stmt = data || {};
//   console.log("data: ", data);
// const bank = stmt?.entity?.bank || {};
// const products = stmt.products || [];
// const summary = stmt.summary || {};


// const authUser = JSON.parse(
//   localStorage.getItem("authUserData") || "{}"
// );

// const gstJson = authUser?.gst_json;

// const hasGST = !!gstJson;

// const taxpayerType =
//   gstJson?.taxpayer_type?.toLowerCase();

// const gstNumber =
//   gstJson?.GSTIN || authUser?.gst || "";

// const isMaharashtraGST = gstNumber.startsWith("27");

// // values
// const subtotal = Number(summary?.subtotal || 0);
// const cgst = Number(summary?.cgst || 0);
// const sgst = Number(summary?.sgst || 0);
// const igst = Number(summary?.igst || 0);
// const tds = Number(summary?.tds || 0);

// // final payable logic
// let payableAmount = subtotal - tds;

// // CASE 2 → Regular + Maharashtra GST
// if (
//   hasGST &&
//   taxpayerType === "regular" &&
//   isMaharashtraGST
// ) {
//   payableAmount =
//     subtotal + cgst + sgst - tds;
// }

// // CASE 3 → Regular + Outside Maharashtra GST
// if (
//   hasGST &&
//   taxpayerType === "regular" &&
//   !isMaharashtraGST
// ) {
//   payableAmount =
//     subtotal + igst - tds;
// }

// const formatNum = (n) =>
//   Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

//   const PRODUCTS = [
//     { name: "Extended Warranty", hsn: "999523", mrp: 500, units: 12, rate: 350 },
//     { name: "Screen Damage Protection", hsn: "999524", mrp: 300, units: 8, rate: 220 },
//     { name: "Total Protection Plan", hsn: "999525", mrp: 600, units: 5, rate: 480 },
//   ];

//   // const GST_RATE = 0.09;
//   // const subtotal = PRODUCTS.reduce((acc, p) => acc + p.rate * p.units, 0);
//   // const cgst = subtotal * GST_RATE;
//   // const sgst = subtotal * GST_RATE;
//   // const total = subtotal - 120;
//   // const formatNum = (n) => n.toLocaleString("en-IN", { minimumFractionDigits: 2 });

//   const [showModal, setShowModal] = useState(false);
//   const pdfRef = useRef(null);


//   const handleDownloadPDF = async () => {
//   try {
//     const input = pdfRef.current;

//     if (!input) return;

//     const canvas = await html2canvas(input, {
//       scale: 2,
//       useCORS: true,
//       backgroundColor: "#ffffff",
//     });

//     const imgData = canvas.toDataURL("image/png");

//     const pdf = new jsPDF("p", "mm", "a4");

//     const pdfWidth = pdf.internal.pageSize.getWidth();

//     const imgHeight =
//       (canvas.height * pdfWidth) / canvas.width;

//     pdf.addImage(
//       imgData,
//       "PNG",
//       0,
//       0,
//       pdfWidth,
//       imgHeight
//     );

//     pdf.save(
//       `${stmt?.statement_id || "payout-summary"}.pdf`
//     );

//   } catch (err) {
//     console.error("PDF download failed", err);
//   }
// };

//   return (
//     <div className="space-y-4">
//       {/* Action Bar */}
//       <div className="flex items-center justify-between">
//         <button onClick={onBack} className="flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors">
//           <ArrowLeft size={16} /> Back to Register
//         </button>
//         <button onClick={handleDownloadPDF} className="flex items-center gap-2 bg-slate-900 text-white text-xs font-bold px-4 py-2 rounded-lg">
//           <Download size={13} /> Download PDF
//         </button>
//       </div>

//       <div
//         ref={pdfRef}
//         className="bg-white border border-slate-200 rounded shadow-sm overflow-hidden text-xs"
//       >

//         {/* Header */}
//         <div className="bg-slate-900 p-8 text-white flex justify-between items-start">
//           <div>
//             <h2 className="text-2xl font-black tracking-tight italic">GOELECTRONIX</h2>
//             <p className="text-slate-400 text-[11px] mt-1 uppercase tracking-widest font-bold">Official Commission Statement</p>
//           </div>
//           <div className="text-right">
//             <p className="text-[11px] font-bold text-slate-400 uppercase">Statement ID</p>
//             <p className="font-mono text-sm">{stmt.statement_id}</p>
//             <p className="font-mono text-sm">{stmt.payout_code}</p>
//           </div>
//         </div>

//         {/* Entity / Company */}
//         <div className="grid grid-cols-2 border-b border-slate-200">
//           {[
//             {
//               logo: User, label: "Entity Details", name: stmt?.entity?.name, badge: stmt?.entity?.type,
//               body: `Type: ${stmt?.entity?.type}\nBank: ${bank?.bank_name }\nPeriod: ${stmt?.cycle?.start_date} to ${stmt?.cycle?.end_date}\nGSTIN: ${stmt?.entity?.gst}`,
//             },
//             {
//               logo: Building2, label: "Company Details", name: stmt?.company?.name, badge: null,
//               body: stmt?.company?.address,
//             },
//           ].map((block, i) => (
//             <div key={i} className={`p-4 ${i === 1 ? "border-l border-slate-200" : ""}`}>
//               <p className="flex items-center gap-1 text-[9px] font-black uppercase tracking-widest text-amber-600 mb-1.5">
//                 {block.logo && <block.logo className="w-3 h-3" />} {block.label}
//               </p>
//               <p className="font-bold text-slate-900 text-[11px] mb-1">
//                 {block.name}
//                 {block.badge && <span className="ml-2 text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">{block.badge}</span>}
//               </p>
//               <p className="text-slate-500 leading-relaxed whitespace-pre-line">{block.body}</p>
//             </div>
//           ))}
//         </div>

//         {/* Product Table */}
//         <table className="w-full text-[10px]">
//           <thead>
//             <tr className="bg-slate-50 border-b border-slate-200">
//               <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500 w-6">#</th>
//               <th className="py-2 px-3 text-left text-[9px] font-black uppercase tracking-wider text-slate-500">Item & Description</th>
//               <th className="py-2 px-3 text-[9px] font-black uppercase tracking-wider text-slate-500">HSN/SAC</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">MRP</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-500">Qty</th>
//               <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-500">Rate</th>
//               {/* <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-blue-50/40" colSpan={2}>CGST</th>
//               <th className="py-2 px-3 text-center text-[9px] font-black uppercase tracking-wider text-slate-400 bg-indigo-50/40" colSpan={2}>SGST</th> */}
//               <th className="py-2 px-3 text-right text-[9px] font-black uppercase tracking-wider text-slate-700">Amount</th>
//             </tr>
//             {/* <tr className="bg-slate-50 border-b border-slate-200">
//               {["","","","","","","%","Amt","%","Amt",""].map((h, i) => (
//                 <th key={i} className="py-1 px-3 text-[9px] font-bold text-slate-400 text-center">{h}</th>
//               ))}
//             </tr> */}
//           </thead>
//           <tbody>
//             {/* {PRODUCTS.map((p, i) => {
//               const lineTotal = p.rate * p.units;
//               const lineCgst = lineTotal * GST_RATE;
//               return (
//                 <tr key={p.name} className="border-b border-slate-100 hover:bg-slate-50/50">
//                   <td className="py-3 px-3 text-slate-500">{i + 1}</td>
//                   <td className="py-3 px-3">
//                     <p className="font-bold text-slate-800">{p.name}</p>
//                     <p className="text-[9px] text-slate-400">Warranty plan for Android smartphones</p>
//                   </td>
//                   <td className="py-3 px-3 font-mono text-slate-600">{p.hsn}</td>
//                   <td className="py-3 px-3 text-center font-mono">{p.mrp}</td>
//                   <td className="py-3 px-3 text-center font-mono">{p.units} pcs</td>
//                   <td className="py-3 px-3 text-right font-mono">{p.rate}.00</td>
//                   <td className="py-3 px-3 text-center font-mono bg-blue-50/20">9%</td>
//                   <td className="py-3 px-3 text-center font-mono bg-blue-50/20">{formatNum(lineCgst)}</td>
//                   <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">9%</td>
//                   <td className="py-3 px-3 text-center font-mono bg-indigo-50/20">{formatNum(lineCgst)}</td>
//                   <td className="py-3 px-3 text-right font-mono font-bold text-slate-800">{formatNum(lineTotal)}</td>
//                 </tr>
//               );
//             })} */}

//             {products.map((p, i) => (
//               <tr key={i}>
//                 <td className="py-3 px-3">{i + 1}</td>

//                 <td className="py-3 px-3">
//                   <p className="font-bold">{p.product_name}</p>
//                   <p className="text-[9px] text-slate-400">Warranty plan</p>
//                 </td>

//                 <td className="py-3 px-3">—</td>
//                 <td className="py-3 px-3 text-center">{p.total_mrp}</td>

//                 <td className="py-3 px-3 text-center">{p.qty}</td>

//                 <td className="py-3 px-3 text-right">{p.rate}</td>

//                 {/* <td className="py-3 px-3 text-center">—</td>
//                 <td className="py-3 px-3 text-center">—</td>

//                 <td className="py-3 px-3 text-center">—</td>
//                 <td className="py-3 px-3 text-center">—</td> */}

//                 <td className="py-3 px-3 text-right font-bold">
//                   {formatNum(p.amount)}
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>

//         {/* Totals */}
//         <div className="grid border-t border-slate-200" style={{ gridTemplateColumns: "1fr 280px" }}>
//           <div className="p-4 border-r border-slate-200">
//             <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Total In Words</p>
//             <p className="font-bold text-slate-800 text-[11px]">Indian Rupee Ten Thousand Only</p>
//           </div>
//           <div className="text-[10px]">
//            {[
//               {
//                 label: "Sub Total",
//                 val: formatNum(subtotal),
//                 cls: "bg-slate-50",
//               },

//               ...(hasGST &&
//               taxpayerType === "regular" &&
//               isMaharashtraGST
//                 ? [
//                     {
//                       label: "CGST (9%)",
//                       val: formatNum(cgst),
//                       cls: "",
//                     },
//                     {
//                       label: "SGST (9%)",
//                       val: formatNum(sgst),
//                       cls: "",
//                     },
//                   ]
//                 : []),

//               ...(hasGST &&
//               taxpayerType === "regular" &&
//               !isMaharashtraGST
//                 ? [
//                     {
//                       label: "IGST (18%)",
//                       val: formatNum(igst),
//                       cls: "",
//                     },
//                   ]
//                 : []),

//               {
//                 label: "TDS Deduction",
//                 val: `- ${formatNum(tds)}`,
//                 cls: "bg-red-50",
//               },
//             ].map(({ label, val, cls }) => (
//               <div key={label} className={`flex justify-between px-5 py-2 border-b border-slate-100 ${cls}`}>
//                 <span className="font-semibold text-slate-600">{label}</span>
//                 <span className="font-mono font-semibold">{val}</span>
//               </div>
//             ))}
//             <div className="flex justify-between px-5 py-3 bg-slate-900">
//               <span className="font-black text-white text-[13px]">Total</span>
//               <span className="font-mono font-black text-blue-400 text-[13px]">₹{formatNum(payableAmount)}</span>
//             </div>
//           </div>
//         </div>

//         {/* Payment TO */}
//         <div className="flex gap-5 p-4 border-t border-slate-200">
//           <div className="w-16 h-16 border-2 border-slate-200 rounded flex items-center justify-center text-[8px] text-slate-400 font-bold bg-slate-50 flex-shrink-0">
//             QR Code
//           </div>
//           <div className="text-[10px] text-slate-500 leading-relaxed">
//             <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-1">Payment TO</p>
//             <p className="font-bold text-slate-800">{stmt?.entity?.name}</p>
//             <p>{bank.bank_name}· ACC NO.:{bank.account_no}</p>
//             <p>IFSC CODE: {bank.ifsc_code} · BRANCH: {bank.branch_name} · ACC TYPE: {bank.account_type}</p>
//           </div>
//         </div>

//         {/* ── SUMMARY VERIFICATION ── */}
//         <div className="mx-4 mb-4 rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50/70 to-white p-4">
//           <div className="flex items-start gap-2 mb-3">
//             <ShieldCheck size={15} className="text-indigo-500 mt-0.5 flex-shrink-0" />
//             <p className="text-[12px] font-semibold text-slate-700 leading-relaxed">
//               Do you agree with this summary? To verify, click on the{" "}
//               <span className="text-indigo-600 font-bold">Verify</span> button.
//             </p>
//           </div>
//           <button
//             onClick={() => setShowModal(true)}
//             className="flex items-center gap-2 px-4 py-2 rounded-lg text-[11px] font-bold text-white transition-all hover:scale-105 active:scale-95"
//             style={{ background: "linear-gradient(135deg,#4f46e5,#6366f1)", boxShadow: "0 2px 8px rgba(99,102,241,0.35)" }}
//           >
//             <CheckCircle2 size={13} /> Verify Summary
//           </button>
//         </div>

//         {/* Footer */}
//         <div className="flex justify-end px-5 py-3 border-t border-slate-200">
//           <span className="font-black text-slate-900">Go<span className="text-blue-500">electronix</span></span>
//         </div>
//       </div>

//       {/* Verify Modal */}
//       {showModal && (
//         <VerifyModal
//           onClose={() => setShowModal(false)}
//           onVerified={onVerified}
//           selectedRowId={data.payout_id} // ✅ correct
          
//         />
//       )}
//     </div>
//   );
// }